import { onlineManager } from "@tanstack/react-query";

import { get, httpDelete, post } from "@readai/common/api";
import type {
  ApiUser,
  SignInUser,
  SignUpUser,
  UserZoomProfile,
} from "@readai/common/types";
import type {
  AuthSessionDetails,
  ConnectedProvider,
  ResetPasswordBody,
} from "@readai/common/types/authTypes";
import type {
  CalendarProviderId,
  LoginMethod,
  SSOProviderId,
} from "@readai/common/types/providerTypes";
import {
  isResponseErrorWithStatus,
  withQueryParamsUrl,
} from "@readai/common/utils";

export const getLoginMethods = async ({
  email,
}: {
  email: string;
}): Promise<LoginMethod[]> => post("/login/methods", { identifier: email });

export const signInUser = async (user: SignInUser): Promise<void> =>
  post("/login/read", user, { withCreds: true });

export const secureSignInUser = async ({
  euid,
  provider,
}: {
  euid: string;
  provider: "zoom";
}): Promise<void> =>
  post(
    "/login/read/provider",
    { encryptedProviderUserId: euid, provider },
    { withCreds: true },
  );

export const signUpUser = async (user: SignUpUser) => {
  try {
    // extract the email verification token, which must be sent as a query string param
    const { emailVerificationToken, ...rest } = user;

    const url = withQueryParamsUrl("/users", {
      email_verification_token: emailVerificationToken,
    });

    return await post(url, rest, { withCreds: true });
  } catch (error) {
    if (isResponseErrorWithStatus(error, 409)) {
      // The message returned from this error is not intended to be displayed in
      // the UI, and thus is not translated.
      //
      // eslint-disable-next-line lingui/no-unlocalized-strings
      return { error: true, message: "Sorry! That email is taken." };
    }
    throw error;
  }
};

export const getConnectedSSO = async (): Promise<ConnectedProvider[]> =>
  get("/users/me/sso", { withCreds: true });

export const deleteConnectedSSO = async (
  providerId: SSOProviderId | CalendarProviderId,
): Promise<boolean> =>
  httpDelete(`/users/me/sso?provider=${providerId}`, {
    withCreds: true,
  });

export const signOutUser = async (revokeAll = false): Promise<boolean> => {
  try {
    await httpDelete(
      withQueryParamsUrl("/users/me/token", { revoke_all: revokeAll }),
      { withCreds: true },
    );
    return true;
  } catch {
    return false;
  }
};

export const deleteAuthSession = async ({
  tokenHash,
}: {
  tokenHash: string;
}): Promise<void> => {
  return httpDelete(
    withQueryParamsUrl(`/users/me/auth-session`, { token_hash: tokenHash }),
    { withCreds: true },
  );
};

export const getAllSessions = async (): Promise<AuthSessionDetails[]> => {
  return get("/users/me/auth-sessions", { withCreds: true });
};

export const getUser = async (options?: {
  authBearer?: string;
}): Promise<ApiUser> => {
  const user = (await get("/users/me", {
    withCreds: true,
    ...options,
  })) as ApiUser;

  // Update online state if we got a successful response. Note that if we
  // change the user fetch call to use useQuery, we'll want to detect
  // the online state using some other mechanism. The gist is that navigator.onLine
  // is not reliable and can return false negatives.
  onlineManager.setOnline(true);

  return user;
};

export const postRequestPasswordReset = async (
  email: string | null,
  recaptchaToken: string | null,
  withCreds = false,
): Promise<void> => {
  return post(
    "/users/request/password-reset",
    { email, recaptchaToken },
    { withCreds },
  );
};

export const postResetPassword = async (body: ResetPasswordBody) => {
  return post("/users/auth/reset-password", {
    token: body.token,
    newPassword: body.newPassword,
    confirmPassword: body.confirmPassword,
  });
};

export const deleteAccount = async () => {
  return httpDelete("/users/me", { withCreds: true });
};

export const getZoomProfile = async (): Promise<UserZoomProfile> => {
  return get("/users/me/zoom_profile", { withCreds: true });
};
